import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import './MatterDetails.css';
import GeneralMatters from './GeneralMatters/GeneralMatters';
import FinancialPage from './Financial/FinancialPage';
import MatterTasksPage from './MatterTasks/MatterTasksPage';
import TopBar from './Components/TopBar';
import { useParams } from 'react-router-dom';
import FileList from './Files/file-list';
import { AdditionalNotesContent } from './AdditionalNotes/AdditionalNotes';
import './AdditionalNotes/AdditionalNotes.css';
import useCurrentEmployee from '@frontend/AppSettings/hooks/useCurrentEmployee';
import MatterService from '@frontend/AppSettings/services/MatterService';
import { isFeatureEnabled } from '@frontend/AppSettings/environmentConfig';
import PipelineProjectDetail from './PipelineProject/PipelineProjectDetail';
import { isPipelineMatter, pipelineRouteQueryKey } from './pipelineMatter';
import '../../BidSheets/bidDetailShared.css';

type MatterDetailsPage = 'general' | 'tasks' | 'financial' | 'files' | 'notes';

// Wrapper for Files content without TopBar
const FilesContent: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  return (
    <div className="filesPage">
      <div>
        <FileList matterId={matterId} />
      </div>
    </div>
  );
};

const MatterDetails: React.FC = () => {
  const { matterId = '' } = useParams();
  const currentEmployee = useCurrentEmployee();
  const permissionsResolved = currentEmployee !== null;
  const permishId = currentEmployee?.permish_id ?? null;
  const rawPermishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  // When no permission is attached, default to the most restrictive view (level 3).
  const permishIdNum =
    rawPermishIdNum === null || rawPermishIdNum === undefined || Number.isNaN(rawPermishIdNum)
      ? 3
      : rawPermishIdNum;
  const isTimesheetOnlyUser = permishIdNum === 2 || permishIdNum === 3;

  const [activePage, setActivePage] = useState<MatterDetailsPage>('general');

  // Fetch first, branch only on positive evidence, dedicated query key so MatterInfo's
  // ['Matters', id] cannot flash-loop (AUTH-N07). Flag off = no fetch, regular chrome.
  const shouldBranch = isFeatureEnabled('bids') && !!matterId;
  const matter = useQuery({
    queryKey: pipelineRouteQueryKey(matterId),
    queryFn: () => MatterService.getMatterDetails(matterId),
    enabled: shouldBranch,
    retry: false,
  });

  const renderActivePage = (): React.ReactNode => {
    switch (activePage) {
      case 'general':
        return <GeneralMatters onOpenFilesTab={() => setActivePage('files')} />;
      case 'tasks':
        return <MatterTasksPage />;
      case 'financial':
        return <FinancialPage />;
      case 'files':
        return <FilesContent />;
      case 'notes':
        return <AdditionalNotesContent />;
      default:
        return <GeneralMatters />;
    }
  };

  if (shouldBranch && matter.isLoading) {
    return (
      <div className="bid-detail-page">
        <div className="bid-detail-notfound">
          <h2>Loading project…</h2>
        </div>
      </div>
    );
  }

  if (shouldBranch && isPipelineMatter(matter.data)) {
    return <PipelineProjectDetail matterid={matterId} matter={matter.data} />;
  }

  if (!permissionsResolved) {
    return (
      <div className="matter-details-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="matter-details-page" data-testid="matter-details">
      <TopBar 
        setActivePage={setActivePage as (page: string) => void} 
        activePage={activePage}
        isTimesheetOnlyUser={isTimesheetOnlyUser}
      />
      <div className="matter-content">{renderActivePage()}</div>
    </div>
  );
};

export default MatterDetails;
